import React, {useState} from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuote } from '../../Data/Quotes';
import TopBar from '../../Components/TopBar';
import Page from '../../Components/Containers/Page';
import { PageLoading } from '../../Components/Loading';
import HeaderImageAndCard from '../../Components/Containers/HeaderImageAndCard';
import { useMapUrl } from '../../Components/Formatting/Map/Map';
import Grid from '../../Components/Formatting/Grid';
import Icon from '../../Components/Icons/Icon';
import NoValue from '../../Components/EmptyStates/NoValue';
import Address from '../../Components/Formatting/Address';
import Tabs, { Tab } from '../../Components/Containers/Tabs/Tabs';
import { usePreference } from '../../Data/Preference';
import Badge from '../../Components/Badge/Badge';
import Thread from '../../Components/Threads';
import PhoneNumber from '../../Components/Formatting/PhoneNumber';
import Email from '../../Components/Formatting/Email';
import Services from '../../Components/Formatting/Services';
import TimeAgo from '../../Components/Formatting/TimeAgo';
import Estimate from './Estimate';
import { getEntityLink, useNewWindow, useRedirect, useVisibilityChange } from '../../Services/helpers';
import CloseQuote from './CloseQuote';
import Text from '../../Components/Formatting/Text';
import ConvertToCustomer from './ConvertToCustomer';
import { useDialogues } from '../../Components/Dialogues';
import Preview from '../../Components/Gallery/Preview';
import { useSession } from '../../Components/Session';
// import EditQuote from './EditQuote';

const Quote = () => {
    const {quoteId} = useParams();
    const [isClosing, setIsClosing] = useState(false);
    const [isConverting, setIsConverting] = useState(false);
    const {quote, updateEstimate, refresh, createItemisation, closeQuote, rejectQuote, offerQuote, convert} = useQuote(quoteId, true);
    const {go, Redirect} = useRedirect();
    // const [editingQuote, setEditingQuote] = useState(false);
    const {confirm} = useDialogues();
    const newWindow = useNewWindow();

    useVisibilityChange(refresh);

    return <>
        <TopBar left="/manage/quotes"
            title={quote?.name ?? 'Quote'}
            dropdownOptions={ quote && [
                // {icon: 'Pencil', title: 'Edit quote details', onClick: () => setEditingQuote(true)},
                // {div: true},
                {icon: 'User', title: 'Convert to customer', onClick: () => setIsConverting(true)},
                {icon: 'BoxDollar', title: 'Sell lead', onClick: () => confirm(
                    {
                        theme: 'primary',
                        title: 'Sell lead',
                        message: <>
                            Sell this lead to another business through the marketplace?<br />
                            You may only sell genuine leads who you have not previously contacted.<br />
                            See the getSoapy help section for full details.
                        </>,
                        confirm: 'I confirm this lead is genuine and all other requirements are met.',
                        confirmLabel: 'Sell',
                    },
                    () => offerQuote().then(async (res) => {
                        refresh();
                        if (res.leadId) {
                            go(getEntityLink('Marketplace/Lead', res.leadId));
                        }
                    })
                )},
                {div: true},
                {icon: 'LinkExternal', title: 'View as customer', onClick: () => newWindow(quote?.customerUrl)},
                {div: true},
                {icon: 'Trash', title: 'Close quote', onClick: () => setIsClosing(true), disabled: quote?.closed},
            ]}
        />
        <Page zeroPad>
            {quote ? <Inner {...{quote, refresh, updateEstimate, createItemisation}} /> : <PageLoading />}
        </Page>

        <CloseQuote {...{isClosing, setIsClosing, quote, refresh, closeQuote, rejectQuote, go}} />
        <ConvertToCustomer {...{isConverting, setIsConverting, quote, refresh, convert, go}} />

        {/* <EditQuote isOpen={editingQuote} quoteId={quoteId} setIsOpen={setEditingQuote} onEdited={refresh} /> */}

        <Redirect />
    </>
}

const Inner = ({quote, refresh, updateEstimate, createItemisation}) => {
    const mapUrl = useMapUrl(quote, true, false);
    const iconStyles = {className: "fill-light", size: "1x", inline: true};
    const [selectedTab, setSelectedTab] = usePreference('quoteTab', 'Jobs');
    const {/* hasFeature,  */isAdmin} = useSession();

    return <>
        <HeaderImageAndCard image={mapUrl}>
            <Grid className="mb-4 lg:max-w-[75%] lg:mx-auto" items={[
                [
                    <Icon icon="User" {...iconStyles} />,
                    <strong>{quote.name ?? <NoValue />}</strong>,
                    !!quote.name
                ],
                [
                    <Icon icon="LocationPin" {...iconStyles} />,
                    quote.address ? <Address address={quote.address} area={quote.area} distance={quote.distance} /> : <NoValue />,
                    !!quote.address
                ],
                [
                    <Icon icon="Phone" {...iconStyles} />,
                    <PhoneNumber phoneNumber={quote.phoneNumber} />,
                    !!quote.phoneNumber
                ],
                [
                    <Icon icon="PaperPlane" {...iconStyles} />,
                    <Email email={quote.email} />,
                    !!quote.email
                ],
                [
                    <Icon icon="Note" {...iconStyles} />,
                    <Text text={quote.notes} />,
                    !!quote.notes,
                ],
                [
                    <Icon icon="User" {...iconStyles} />,
                    <Link to={getEntityLink('Customers/Customer', quote.customerId)} className="link">
                        View customer
                    </Link>,
                    !!quote.customerId
                ],
                [
                    <Icon theme="solid"
                        icon={quote.status === 'Accepted' ? 'ThumbsUp' : 'ThumbsDown'}
                        className={quote.status === 'Accepted' ? 'fill-green-600' : 'fill-red-600'}
                    />,
                    <strong className={quote.status === 'Accepted' ? 'text-green-600' : 'text-red-600'}>
                        {quote.status}
                    </strong>,
                    ['Accepted', 'Declined'].indexOf(quote.status) !== -1
                ]
            ]} />
        </HeaderImageAndCard>

        <div className="page-padding">
            <Tabs selected={selectedTab} onChange={setSelectedTab}>
                <Tab title="Details" icon="Info">
                    <Grid className="mb-4 lg:max-w-[75%] lg:mx-auto" items={[
                        [
                            <strong>Status</strong>,
                            <>
                                {quote.status}
                                {quote.closed ? ' (Closed)' : null}
                            </>,
                        ],
                        [
                            <strong>Services</strong>,
                            <Services services={quote.services} />,
                        ],
                        // Don't use ?? as many of these will be '' not null
                        [
                            <strong>Availability</strong>,
                            quote.availability ? quote.availability.replace(',', ', ') : <NoValue />,
                        ],
                        [
                            <strong>Frequency</strong>,
                            quote.frequency ? quote.frequency : <NoValue />,
                        ],
                        [
                            <strong>Building</strong>,
                            quote.buildingType ? quote.buildingType : <NoValue />,
                        ],
                        [
                            <strong>Bedrooms</strong>,
                            quote.bedrooms ? quote.bedrooms : <NoValue />,
                        ],
                        [
                            <strong>Notes</strong>,
                            quote.notes ? <Text text={quote.notes} /> : <NoValue />,
                        ],
                        [
                            <strong>Requested</strong>,
                            <TimeAgo dateTime={quote.submittedAt} />,
                        ],
                        [
                            <strong>Attachments</strong>,
                            <Preview assetIds={quote.assetIds} />,
                            // hasFeature('ATTACHMENTS')
                            isAdmin()
                        ]
                    ]} />
                </Tab>
                <Tab title="Estimates" icon="ClipboardCheck">
                    <Estimate {...{quote, refresh, updateEstimate, createItemisation}} />
                </Tab>
                <Tab title="Messages" icon="Comment" badge={quote.unreadMessages > 0 && <Badge theme="danger" title={quote.unreadMessages} className="ml-2" />}>
                    <Messages quote={quote} />
                </Tab>
            </Tabs>
        </div>
    </>
}

const Messages = ({quote}) => {
    if (quote.threadId) {
        return <Thread threadId={quote.threadId} />
    }

    if (quote.customerId) {
        return <p className="text-muted text-center my-4">
            Use the customer messages to communicate with this customer.
        </p>
    }

    return <NoValue />
}

export default Quote;
